import { z } from 'zod';
import {
  FINPSP_BANK_TYPES,
  FINPSP_CURRENCY,
  FINPSP_DEPOSIT_TYPE,
  FINPSP_WITHDRAWAL_TYPE,
  FINPSP_DEPOSIT_CHANNELS,
  FINPSP_PAYMENT_STATUS,
  FINPSP_PAYMENT_STATE,
} from '../constants/finpsp';

export const finpspPaymentSchema = z.object({
  transactionId: z.uuid(),
  orderId: z.string().min(1),
  amount: z.number().positive(),
  currency: z.literal(FINPSP_CURRENCY),
  bank_type: z.enum(FINPSP_BANK_TYPES),
  status: z.enum(Object.values(FINPSP_PAYMENT_STATUS)),
  state: z.enum(Object.values(FINPSP_PAYMENT_STATE)),
  request_id: z.string().optional(),
  redirect_url: z.string().nullish(),
  sim_number: z.string().trim().nullish(),
  sim_type: z.string().nullish(),
  type: z.string().nullish(),
  assignment_status: z.string().nullish(),
  expires_at: z.iso.datetime({ offset: true }).nullish(),
  providerStatus: z.string().optional(),
});

export const finpspInitiationSchema = z.union([
  z.object({
    authResults: z
      .array(
        z.object({
          limitName: z.string().min(1),
          limitValue: z.number().optional(),
          newValue: z.number().optional(),
        })
      )
      .min(1),
  }),
  finpspPaymentSchema,
]);

type Translate = (key: string) => string;

const createFinpspAmountSchema = (t: Translate) =>
  z.object({
    amount: z
      .string()
      .trim()
      .min(1, t('requiredField'))
      .regex(/^(0|[1-9]\d{0,11})(\.\d{1,2})?$/, t('finpspAmountInvalid'))
      .transform(Number)
      .refine((amount) => amount > 0, t('amountMustBeGreaterThanZero'))
      .refine((amount) => amount <= 999999999999, t('finpspAmountInvalid')),
  });

const createFinpspPhoneSchema = (t: Translate) =>
  z
    .string()
    .trim()
    .min(1, t('requiredField'))
    .transform((phone) => phone.replace(/^\+?880/, '0'))
    .refine((phone) => /^01[3-9]\d{8}$/.test(phone), t('invalidPhoneNumber'));

export const createFinpspDepositSchema = (t: Translate) => {
  const phone = createFinpspPhoneSchema(t);

  return createFinpspAmountSchema(t)
    .extend({
      type: z.enum(Object.values(FINPSP_DEPOSIT_TYPE)),
      customer_number: z
        .string()
        .trim()
        .transform((value) => value.replace(/^\+?880/, '0'))
        .optional(),
    })
    .superRefine((input, context) => {
      if (!FINPSP_DEPOSIT_CHANNELS[input.type].requiresCustomerNumber) {
        return;
      }

      const validation = phone.safeParse(input.customer_number ?? '');

      if (!validation.success) {
        for (const issue of validation.error.issues) {
          context.addIssue({ ...issue, path: ['customer_number'] });
        }
      }
    });
};

export const createFinpspWithdrawalSchema = (t: Translate) =>
  createFinpspAmountSchema(t).extend({
    type: z.enum(Object.values(FINPSP_WITHDRAWAL_TYPE)),
    customer_phone: createFinpspPhoneSchema(t),
  });
